import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { PASSWORD_MIN_LENGTH, type MeDto } from '@bingo/shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useLang } from '../lib/i18n';
import { Brand } from '../components/Brand';
import { LangSwitch } from '../components/LangSwitch';
import { PasswordInput } from '../components/PasswordInput';

export function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { setMe } = useAuth();
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (password.length < PASSWORD_MIN_LENGTH) return setError(t('Use at least {n} characters.', { n: PASSWORD_MIN_LENGTH }));
    if (password !== repeat) return setError(t('The two passwords are different.'));
    setBusy(true);
    try {
      let who = await api<MeDto>('/api/auth/set-password', { method: 'POST', body: { token, password } });
      if (who.user.language !== lang) who = await api<MeDto>('/api/me/preferences', { method: 'PATCH', body: { language: lang } });
      setMe(who);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('Could not save the password.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-page">
      <form className="card auth-card" onSubmit={submit}>
        <div className="auth-top">
          <Brand />
          <LangSwitch />
        </div>
        <h1>{t('Choose your password')}</h1>
        {!token && <div className="alert alert-bad">{t('This link is incomplete. Open the full link you received.')}</div>}
        {error && <div className="alert alert-bad" role="alert">{error}</div>}
        <div className="field">
          <label htmlFor="pw">{t('New password')}</label>
          <PasswordInput id="pw" className="input" autoComplete="new-password" required
            value={password} onChange={(e) => setPassword(e.target.value)} />
          <span className="hint">{t('At least {n} characters. A short sentence is easy to remember.', { n: PASSWORD_MIN_LENGTH })}</span>
        </div>
        <div className="field">
          <label htmlFor="pw2">{t('Repeat the password')}</label>
          <PasswordInput id="pw2" className="input" autoComplete="new-password" required
            value={repeat} onChange={(e) => setRepeat(e.target.value)} />
        </div>
        <button className="btn btn-primary btn-block" disabled={busy || !token}>{busy ? t('Saving…') : t('Save and log in')}</button>
      </form>
    </div>
  );
}
